// @flow
import React from 'react';
import { connect } from 'react-redux';
import DownloadCrossOriginAsset from 'app/common/react/components/DownloadCrossOriginAsset/DownloadCrossOriginAsset';
import DropdownOption from 'app/common/react/ui/experimental/DropdownOption';
import feedbackHelpers from 'app/packages/pin-utils/feedbackHelpers';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import pwaType from 'app/common/lib/pwaType';
import type { Router } from 'app/common/lib/ReactRouter/types';
import { compose } from 'redux';
import { INTEREST_LIST_FAVORITED } from 'app/packages/pinterest-constants';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { ReasonToChoose } from '@pinterest-web/schemas/enums/fantasio_commons';
import { track } from 'app/common/lib/react/analytics';
import { withRouter } from 'react-router';
import showPinFeedback, {
  showPinFeedbackUndo,
  showPinFeedbackOverlay,
  hidePinFeedback,
} from 'app/common/redux/actions/pin/feedback';
import type {
  FeedbackType,
  FeedbackTextType,
} from 'app/common/react/components/PinRep/feedback-types';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Divider, Image, Mask, Text, Touchable } from 'gestalt';
import { callPinFeedbackAction, undoActionList } from 'app/packages/pin-utils/feedbackActions';

type Props = {
  ad_match_reason?: number,
  bestPinImgSrc: string,
  board: {
    followed_by_me: boolean,
    id: string,
    image_thumbnail_url: string,
    name: string,
    url: string,
  },
  dispatch: Function,
  feedbackData: Object,
  feedbackType: FeedbackType,
  hideDownload: boolean,
  location: Location,
  onHide: () => void,
  onLinkClick: () => void,
  pinCategory?: string,
  pinDescription?: string,
  pinId: string,
  pinImgSrc: string,
  pinTitle?: string,
  pinner: {
    explicitly_followed_by_me: boolean,
    full_name: string,
    id: string,
    image_small_url: string,
    username: string,
  },
  promoter?: {
    full_name: string,
    username: string,
  },
  recommendationReason: {
    board?: {
      id: string,
      name: string,
      url: string,
    },
    interest?: {
      id: string,
    },
    pin?: {
      id: string,
    },
    reason?: string,
    reason_id?: string,
    reason_image?: {
      '236x': {
        height: number,
        url: string,
        width: number,
      },
    },
  },
  reportPin: () => void,
  requestContext: RequestContextType,
  router: Router,
  showRelatedPinsFeedback: boolean,
  sourceInterest?: {
    id: string,
    name: string,
    url_name: string,
  },
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
};

type actionOptionType = {
  complaint_reason?: number,
  feedback_type?: number,
  interest_id?: string,
  pin_id?: string,
  promoted?: boolean,
  recommendation_reason_id?: number,
  through_id?: string,
  undo?: boolean,
  undoText?: string,
  user_id?: string,
};

type OptionType = {|
  action?: string,
  actionOptions?: actionOptionType,
  auxData?: {
    feedback_type?: string,
  },
  callback?: Function,
  description?: string,
  elementType?: number,
  feedbackText?: FeedbackTextType,
  link?: string,
  text?: string,
|};

type State = {|
  pinFeedbackConfig: {
    description?: string,
    options: Array<OptionType>,
  },
  showSecondary: boolean,
|};

const lightNavigateLinkStyle = {
  color: 'white',
  textDecorationColor: 'white',
};

export class PinFeedbackDropdown extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const pinFeedbackConfig = this._pinFeedbackText();
    this.state = {
      pinFeedbackConfig,
      showSecondary: false,
    };
  }

  onPinFeedbackActionButtonClick(
    event: SyntheticEvent<>,
    rowOption: OptionType,
    callback: Function,
  ) {
    event.stopPropagation();
    if (rowOption.auxData) {
      this.handleClickLogging(rowOption.elementType, rowOption.auxData);
    } else {
      this.handleClickLogging(rowOption.elementType);
    }
    callPinFeedbackAction(rowOption.action, rowOption.actionOptions, callback);
  }

  onPinHide(
    feedbackText: FeedbackTextType,
    canUndo: boolean,
    actionOptions?: actionOptionType,
    action?: string,
  ) {
    const { dispatch, onHide, pinId } = this.props;

    dispatch(
      canUndo && actionOptions && action
        ? showPinFeedbackUndo(
            pinId,
            feedbackText,
            this.generateUndoHideCallback(actionOptions, action),
            this.getUndoText(action),
          )
        : showPinFeedback(pinId, feedbackText),
    );

    if (
      actionOptions &&
      !actionOptions.promoted &&
      (actionOptions.feedback_type === PCONST.thrift.PinFeedbackTypes.BLOCK_ONLY_THIS_PIN ||
        actionOptions.feedback_type === PCONST.thrift.PinFeedbackTypes.BLOCK_PFY_THROUGH_PIN)
    ) {
      this.setState({
        showSecondary: true,
      });
      dispatch(showPinFeedbackOverlay(pinId));
    } else {
      onHide();
    }
  }

  getThroughId() {
    const { recommendationReason } = this.props;
    if (recommendationReason.reason_id) {
      return recommendationReason.reason_id;
    } else if (recommendationReason.board) {
      return recommendationReason.board.id;
    } else if (recommendationReason.pin) {
      return recommendationReason.pin.id;
    } else if (recommendationReason.interest) {
      return recommendationReason.interest.id;
    } else {
      return '';
    }
  }

  getComponentType() {
    const { feedbackType } = this.props;
    switch (feedbackType) {
      case 'promoted':
        return PCONST.thrift.ComponentType.PIN_FEEDBACK_DIALOG_PROMOTED;
      case 'followed':
      case 'topic':
        return PCONST.thrift.ComponentType.PIN_FEEDBACK_PIN_DIALOG_INTEREST;
      case 'pfyBoard':
      case 'pfy':
        return PCONST.thrift.ComponentType.PIN_FEEDBACK_DIALOG_PFY;
      default:
        break;
    }
    return undefined;
  }

  getRecommendationDescription = () => {
    const {
      ad_match_reason,
      board,
      feedbackType,
      pinner,
      promoter,
      recommendationReason,
      sourceInterest,
    } = this.props;
    return this.feedbackHelpers.getRecommendationDescription({
      ad_match_reason,
      board,
      feedbackType,
      makeNavigateLink: this.makeNavigateLink,
      pinner,
      promoter,
      recommendationReason,
      sourceInterest,
    });
  };

  getRecommendationDescriptionImage = () => {
    const { board, feedbackType, pinner: user, recommendationReason } = this.props;

    if (feedbackType !== 'followed' && feedbackType !== 'pfyBoard') {
      return null;
    }

    let imageUrl;
    let borderRadius;
    let url;

    if (user && user.explicitly_followed_by_me) {
      imageUrl = user.image_small_url;
      borderRadius = '50%';
      url = `/${user.username}`;
    } else if (board && board.image_thumbnail_url) {
      imageUrl = board.image_thumbnail_url;
      borderRadius = '4px';
      url =
        (recommendationReason && recommendationReason.board && recommendationReason.board.url) ||
        board.url;
    }

    if (imageUrl) {
      return (
        <Box
          marginStart={3}
          flex="none"
          dangerouslySetInlineStyle={{ __style: { borderRadius } }}
          overflow="hidden"
        >
          <Touchable onTouch={() => this.props.router.push(url)}>
            <Mask width={40} height={40} wash>
              <Image src={imageUrl} naturalHeight={1} naturalWidth={1} alt="" fit="cover" />
            </Mask>
          </Touchable>
        </Box>
      );
    }

    return null;
  };

  getUnfollowAction = () => {
    const { board, feedbackType, pinner, sourceInterest } = this.props;
    return this.feedbackHelpers.getUnfollowAction({
      board,
      feedbackType,
      makeNavigateLink: this.makeNavigateLink,
      pinner,
      sourceInterest,
    });
  };

  getUndoText = (action: string) =>
    action === 'unfollowPinner' || action === 'unfollowBoard'
      ? i18n._('Follow again', 'Caption for button to undo a unfollowing of a topic')
      : '';

  generateUndoHideCallback = (actionOptions: actionOptionType, action: string) => () => {
    const { dispatch, pinId, viewType, viewParameter } = this.props;

    const undoActionOptions = {
      ...actionOptions,
      undo: true,
    };

    // Specifying undo unfollow actions with corresponding follow actions
    let undoAction = action;
    if (action === 'unfollowPinner') {
      undoAction = 'followPinner';
    } else if (action === 'unfollowBoard') {
      undoAction = 'followBoard';
    } else if (action === 'unfollowTopic') {
      undoAction = 'followTopic';
    }

    //@jlum call the dispatch as a callback or optimistically show?
    callPinFeedbackAction(undoAction, undoActionOptions);
    dispatch(hidePinFeedback(pinId));

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.FLOWED_PIN,
      element: PCONST.thrift.ElementType.PIN_FEEDBACK_UNDO_HIDE,
      view: viewType,
      viewParameter,
    });
  };

  feedbackHelpers = feedbackHelpers({
    i18n,
    interpolateNamedTemplate,
  });

  makeStrongText(key: string, text: any) {
    return <strong key={key}>{text}</strong>;
  }

  makeNavigateLink = (key: string, link: string, text: string, isLight?: boolean) => {
    const linkStyle = {
      display: 'inline',
      ...(isLight ? lightNavigateLinkStyle : {}),
    };

    return (
      <NavigateLink className="underlineLink" key={key} to={link} onClick={this.props.onLinkClick}>
        <Box dangerouslySetInlineStyle={{ __style: linkStyle }}>{text}</Box>
      </NavigateLink>
    );
  };

  reportPinOption = () => ({
    action: 'reportPin',
    elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_BUTTON_REPORT_PIN,
    text: i18n._('Report Pin', 'Report a pin in the dropdown'),
    description: i18n._(
      "This goes against Pinterest's community guidelines",
      'Description for the report pin option',
    ),
    callback: this.props.reportPin,
  });

  hidePinOption = (canUndo: boolean) => {
    const { feedbackData, feedbackType, pinId, recommendationReason } = this.props;
    const elementType =
      feedbackType === 'search'
        ? PCONST.thrift.ElementType.PIN_FEEDBACK_BUTTON_REPORT_UNRELATED_PIN
        : PCONST.thrift.ElementType.PIN_HIDE_BUTTON;
    const { action, actionOptions } = this.feedbackHelpers.getHidePinAction({
      feedbackData,
      feedbackType,
      pinId,
      recommendationReasonId:
        recommendationReason &&
        recommendationReason.reason &&
        ReasonToChoose[recommendationReason.reason],
      pinFeedbackType:
        feedbackType === 'pfy'
          ? PCONST.thrift.PinFeedbackTypes.BLOCK_PFY_THROUGH_PIN
          : PCONST.thrift.PinFeedbackTypes.BLOCK_ONLY_THIS_PIN,
      throughId: recommendationReason && this.getThroughId(),
    });

    const text = i18n._('Hide Pin', 'message displayed in pin feedback list');

    return {
      action,
      actionOptions,
      feedbackText: this.pinHiddenFeedbackText(canUndo),
      text,
      description: i18n._('See fewer Pins like this', 'Description for the hide pin option'),
      elementType,
    };
  };

  stopRecommendationOption = () => {
    const { feedbackData, feedbackType, pinId, recommendationReason } = this.props;

    const elementType = PCONST.thrift.ElementType.PIN_FEEDBACK_BUTTON_PFY;
    const { board, reason } = recommendationReason;
    const recommendationReasonId = reason && ReasonToChoose[reason];
    const throughId = recommendationReason && this.getThroughId();

    const isBoardPfy = feedbackType === 'pfyBoard';

    const { action, actionOptions } = this.feedbackHelpers.getHidePinAction({
      feedbackData,
      feedbackType,
      pinId,
      recommendationReasonId,
      pinFeedbackType: isBoardPfy
        ? PCONST.thrift.PinFeedbackTypes.BLOCK_PFY_THROUGH_BOARD
        : PCONST.thrift.PinFeedbackTypes.BLOCK_PFY_THROUGH_PIN,
      throughId,
    });

    const lightFormattedBoard =
      !!board && this.makeNavigateLink('boardUrl', board.url, board.name, true);
    const subTitle =
      isBoardPfy && !!board
        ? interpolateNamedTemplate(
            i18n._(
              "We won't show you Pins inspired by your board {{ formattedBoard }} anymore.",
              'Primary caption displayed in pin feedback list to unfollow a board',
            ),
            { formattedBoard: lightFormattedBoard },
          )
        : i18n._(
            "Got it! We won't show you Pins like this in the future.",
            'message displayed after stopping recommendations from a pin',
          );

    const text =
      isBoardPfy && !!board
        ? interpolateNamedTemplate(
            i18n._(
              'Stop seeing Pins inspired by {{ boardName }}',
              'Primary caption displayed in pin feedback list to unfollow a board',
            ),
            { boardName: board.name },
          )
        : i18n._(
            'See fewer Pins like this',
            'Feedback option to stop recommendations inspired by this Pin',
          );

    return {
      action,
      actionOptions,
      feedbackText: {
        subTitle,
      },
      text,
      elementType,
    };
  };

  unfollowBoardOption = () => {
    const { board } = this.props;
    const elementType = PCONST.thrift.ElementType.PIN_FEEDBACK_BUTTON_UNFOLLOW_BOARD;

    const formattedBoard = this.makeNavigateLink('boardUrl', board.url, board.name, true);

    const text = interpolateNamedTemplate(
      i18n._(
        'Unfollow {{ boardName }}',
        'Primary caption displayed in pin feedback list to unfollow a board',
      ),
      { boardName: board.name },
    );

    const subTitle = interpolateNamedTemplate(
      i18n._(
        "Got it! You unfollowed {{ formattedBoard }} and won't see Pins saved to it in the future.",
        'Confirmation text after unfollowing a board',
      ),
      { formattedBoard },
    );

    return {
      action: 'unfollowBoard',
      actionOptions: {
        board_id: board.id,
      },
      feedbackText: {
        title: '',
        subTitle,
      },
      text,
      description: interpolateNamedTemplate(
        i18n._(
          'Stop seeing Pins from the board "{{ boardName }}"',
          'Secondary caption displayed in pin feedback list to unfollow a board',
        ),
        { boardName: board.name },
      ),
      elementType,
    };
  };

  unfollowTopicOption = () => {
    const { sourceInterest } = this.props;
    const elementType = PCONST.thrift.ElementType.PIN_FEEDBACK_BUTTON_INTEREST;

    if (sourceInterest) {
      const { id, name: topicName, url_name: urlName } = sourceInterest;
      const url = `/topics/${urlName}`;
      const formattedTopic = this.makeNavigateLink('topicUrl', url, topicName, true);

      const text = interpolateNamedTemplate(
        i18n._(
          'See fewer Pins from {{ topicName }}',
          'Primary caption displayed in pin feedback list to unfollow a topic',
        ),
        { topicName },
      );

      const subTitle = interpolateNamedTemplate(
        i18n._(
          "We'll show you fewer Pins from {{ formattedTopic }} in the future.",
          'Confirmation text after unfollowing a topic',
        ),
        { formattedTopic },
      );

      return {
        action: 'unfollowTopic',
        actionOptions: {
          interest_id: id,
          interest_list: INTEREST_LIST_FAVORITED,
        },
        feedbackText: {
          title: '',
          subTitle,
        },
        text,
        description: interpolateNamedTemplate(
          i18n._(
            'Stop seeing Pins from the topic "{{ topicName }}"',
            'Secondary caption displayed in pin feedback list to unfollow a topic',
          ),
          { topicName: sourceInterest.name },
        ),
        elementType,
      };
    } else {
      //it should never get here
      return null;
    }
  };

  unfollowUserOption = () => {
    const { pinner: user } = this.props;
    const elementType = PCONST.thrift.ElementType.PIN_FEEDBACK_BUTTON_UNFOLLOW_USER;

    const { id, username, full_name: fullName } = user;

    const userUrl = `/${username}`;
    const formattedUser = this.makeNavigateLink('pinnerUrl', userUrl, fullName, true);

    const subTitle = interpolateNamedTemplate(
      i18n._(
        "Got it! You unfollowed {{ formattedUser }} and won't see Pins they save in the future.",
        'Confirmation text after unfollowing a user',
      ),
      { formattedUser },
    );

    return {
      action: 'unfollowPinner',
      actionOptions: {
        user_id: id,
      },
      feedbackText: {
        title: '',
        subTitle,
      },
      text: interpolateNamedTemplate(
        i18n._(
          'Unfollow {{ fullName }}',
          'Primary caption displayed in pin feedback list to unfollow a user',
        ),
        { fullName },
      ),
      description: i18n._(
        'Stop seeing all their Pins',
        'Secondary caption displayed in pin feedback list to unfollow a user',
      ),
      elementType,
    };
  };

  downloadPinOption = () => ({
    action: 'downloadPin',
    text: i18n._('Download image', 'Button to download Pin image'),
    elementType: PCONST.thrift.ElementType.PIN_DOWNLOAD_BUTTON,
  });

  _pinFeedbackText = () => {
    const { location, feedbackType, hideDownload, pinner } = this.props;
    const followingBoard = this.props.feedbackType === 'followed';
    const inFollowingPage = location.pathname.includes('following');

    const canUndo = (followingBoard || feedbackType === 'topic') && !inFollowingPage;
    const hidePin = this.hidePinOption(canUndo);
    const reportPin = this.reportPinOption();

    const showDownloadPinOption = feedbackType !== 'promoted' && !hideDownload;

    switch (feedbackType) {
      case 'followed':
        // We only display direct unfollow options on the following feed
        const showUnfollowOptions = inFollowingPage;
        const description = !showUnfollowOptions && this.getRecommendationDescription();

        const unfollowOption = pinner.explicitly_followed_by_me
          ? this.unfollowUserOption()
          : this.unfollowBoardOption();
        const followedOptions = [
          hidePin,
          ...(canUndo || showUnfollowOptions ? [unfollowOption] : []),
          ...(feedbackType !== 'promoted' ? [this.downloadPinOption()] : []),
          reportPin,
        ];

        // We disable the reason if we're showing direct unfollow board and unfollow user options
        return {
          ...(description ? { description } : {}),
          options: followedOptions,
        };

      case 'promoted':
        const { ad_match_reason } = this.props;
        const learnMore = {
          link: this.feedbackHelpers.getLearnMoreUrl(ad_match_reason),
          text: i18n._('Why am I seeing this ad?', 'Link to promoted pins explanation page'),
        };

        const promotedOptions = [hidePin, learnMore, reportPin];

        return {
          description: this.getRecommendationDescription(),
          options: promotedOptions,
        };

      case 'topic':
        const topicOption = this.unfollowTopicOption();
        const topicOptions = [
          hidePin,
          ...(canUndo && topicOption ? [topicOption] : []),
          ...(showDownloadPinOption ? [this.downloadPinOption()] : []),
          reportPin,
        ];

        return {
          description: this.getRecommendationDescription(),
          options: topicOptions,
        };

      case 'pfy':
      case 'pfyBoard':
        const pfyOptions = [
          hidePin,
          ...(feedbackType === 'pfyBoard' ? [this.stopRecommendationOption()] : []),
          ...(showDownloadPinOption ? [this.downloadPinOption()] : []),
          reportPin,
        ];

        return {
          description: this.getRecommendationDescription(),
          options: pfyOptions,
        };

      default:
        const defaultOptions = [
          hidePin,
          ...(showDownloadPinOption ? [this.downloadPinOption()] : []),
          reportPin,
        ];
        return {
          description: this.getRecommendationDescription(),
          options: defaultOptions,
        };
    }
  };

  handleClickLogging(elementType?: number, auxData?: Object = {}) {
    const { viewParameter, viewType } = this.props;
    track(PCONST.thrift.EventType.CLICK, {
      ...auxData,
      view: viewType,
      viewParameter,
      element: elementType,
      component: this.getComponentType(),
    });
  }

  handleSecondaryFeedbackItemClick = (
    complaintReason: $Values<PCONST.thrift.RecommendationComplaintReason>,
    elementType: $Values<PCONST.thrift.ElementType>,
  ) => {
    const { feedbackData, feedbackType, pinId, onHide, recommendationReason } = this.props;
    const recommendationReasonId =
      recommendationReason &&
      recommendationReason.reason &&
      ReasonToChoose[recommendationReason.reason];
    const throughId = recommendationReason && this.getThroughId();
    const { action, actionOptions } = this.feedbackHelpers.getHidePinAction({
      feedbackData,
      feedbackType,
      pinId,
      recommendationReasonId,
      throughId,
      complaintReason,
    });

    this.handleClickLogging(elementType);

    callPinFeedbackAction(action, actionOptions);
    onHide();
  };

  pinHiddenFeedbackText = (canUndo: boolean) => {
    const unfollow = !canUndo ? this.getUnfollowAction() : null;
    if (unfollow) {
      return {
        title: '',
        subTitle: this.getRecommendationDescription(),
        unfollow,
      };
    }
    // TODO: When ./PinFeedbackText.js is translated,
    // replace this default value with the result of PinFeedbackText.PIN_HIDDEN
    return {
      title: '',
      subTitle: i18n._(
        "Got it! We won't show you this Pin in the future.",
        'message displayed after hiding a pin',
      ),
    };
  };

  renderButtons() {
    const { hideDownload } = this.props;
    const { pinFeedbackConfig } = this.state;
    const { options } = pinFeedbackConfig;

    const rows = [];

    const numOptions = options.length;

    for (let i = 0; i < numOptions; i += 1) {
      const rowOption = options[i];
      const canUndo = undoActionList.includes(rowOption.action);

      const callback = rowOption.callback
        ? rowOption.callback
        : () =>
            this.onPinHide(
              rowOption.feedbackText,
              canUndo,
              rowOption.actionOptions,
              rowOption.action,
            );
      if (rowOption.action !== 'downloadPin') {
        rows.push(
          <Box key={i}>
            {rowOption.link ? (
              <NavigateLink external to={rowOption.link}>
                <DropdownOption text={rowOption.text} compact />
              </NavigateLink>
            ) : (
              <Touchable
                onTouch={({ event }) =>
                  this.onPinFeedbackActionButtonClick(event, rowOption, callback)
                }
                fullWidth={false}
              >
                <DropdownOption text={rowOption.text} compact />
              </Touchable>
            )}
          </Box>,
        );
      } else if (!hideDownload && typeof window !== 'undefined' && pwaType(window) !== 'windows') {
        const { bestPinImgSrc, pinTitle, pinDescription, pinCategory } = this.props;

        rows.push(
          <Box key={i}>
            <DownloadCrossOriginAsset
              imgSrc={bestPinImgSrc}
              filename={pinTitle || pinDescription}
              category={pinCategory}
            >
              {(onEvent) => (
                <Touchable onTouch={onEvent} fullWidth={false}>
                  <DropdownOption
                    text={i18n._('Download image', 'Button to download Pin image')}
                    compact
                  />
                </Touchable>
              )}
            </DownloadCrossOriginAsset>
          </Box>,
        );
      }
    }

    return rows;
  }

  renderSecondaryFeedbackItem = ({
    text,
    reason,
    elementType,
  }: {
    elementType: $Values<PCONST.thrift.ElementType>,
    reason: $Values<PCONST.thrift.RecommendationComplaintReason>,
    text: string,
  }) => (
    <Touchable
      onTouch={() => this.handleSecondaryFeedbackItemClick(reason, elementType)}
      fullWidth={false}
      key={text}
    >
      <DropdownOption text={text} compact />
    </Touchable>
  );

  renderSecondaryFeedback = () => {
    const { feedbackType } = this.props;

    const options =
      feedbackType === 'promoted'
        ? [
            {
              text: i18n._('Not relevant to me', 'Feedback option for Pin'),
              reason: PCONST.thrift.RecommendationComplaintReason.NOT_MY_TASTE_OR_STYLE,
              elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_REASON_NOT_MY_TASTE,
            },
            {
              text: i18n._('Seen Pin too many times', ''),
              reason: PCONST.thrift.RecommendationComplaintReason.SEEN_IT_BEFORE,
              elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_REASON_REPETITIVE_AD,
            },
            {
              text: i18n._('Blurry or pixelated image', 'low quality picture'),
              reason: PCONST.thrift.RecommendationComplaintReason.LOW_QUALITY,
              elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_REASON_LOW_QUALITY,
            },
          ]
        : [
            {
              text: i18n._('Not relevant to me', 'Feedback option for Pin'),
              reason: PCONST.thrift.RecommendationComplaintReason.NOT_MY_TASTE_OR_STYLE,
              elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_REASON_NOT_MY_TASTE,
            },
            {
              text: i18n._('Not in a language I understand', ''),
              reason: PCONST.thrift.RecommendationComplaintReason.NOT_MY_LANGUAGE,
              elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_REASON_NOT_MY_LANGUAGE,
            },
            {
              text: i18n._('Seen Pin too many times', ''),
              reason: PCONST.thrift.RecommendationComplaintReason.SEEN_IT_BEFORE,
              elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_REASON_BUTTON_SEEN_BEFORE,
            },
            {
              text: i18n._('Blurry or pixelated image', 'low quality picture'),
              reason: PCONST.thrift.RecommendationComplaintReason.LOW_QUALITY,
              elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_REASON_LOW_QUALITY,
            },
            {
              text: i18n._('Other', ''),
              reason: PCONST.thrift.RecommendationComplaintReason.MISSING_REASON,
              elementType: PCONST.thrift.ElementType.PIN_FEEDBACK_REASON_OTHER,
            },
          ];

    return (
      <Box overflow="hidden" width="100%" shape="rounded">
        <Box paddingX={4} paddingY={4}>
          <Text color="darkGray" size="sm">
            {i18n._(
              'Got a moment? Tell us why.',
              'Title of follow up question after negative Pin feedback',
            )}
          </Text>
        </Box>
        <Divider />
        <Box paddingY={2}>{options.map(this.renderSecondaryFeedbackItem)}</Box>
      </Box>
    );
  };

  render() {
    const { pinFeedbackConfig, showSecondary } = this.state;
    const { description } = pinFeedbackConfig;

    const descriptionImage = this.getRecommendationDescriptionImage();

    return (
      <Box overflow="hidden" width="100%">
        {showSecondary ? (
          this.renderSecondaryFeedback()
        ) : (
          <Box>
            {description && (
              <Box>
                <Box padding={4} display="flex" alignItems="center">
                  <Box maxHeight={56} overflow="hidden">
                    <Text color="darkGray" size="sm" overflow="normal">
                      {description}
                    </Text>
                  </Box>
                  {descriptionImage}
                </Box>
                <Divider />
              </Box>
            )}
            <Box marginTop={2} marginBottom={3}>
              {this.renderButtons()}
            </Box>
          </Box>
        )}
      </Box>
    );
  }
}

export default compose(
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  connect<*, *, _, _, _, _>(),
  withRequestContext,
  withRouter,
)(PinFeedbackDropdown);
